import { zodResolver } from '@hookform/resolvers/zod';
import {
  ITeam,
  ITeamSchema,
  IUser,
  TeamSchema,
  UserStatusEnum,
  UserTypeEnum,
} from '@monetize/types/app';
import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateTeam,
  useUpdateTeam,
} from '../../../../../api/settingsService';
import {
  MAvatar,
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
} from '../../../../../components/Monetize';
import { UserAvatarAndName } from '../../../../../components/Monetize/MCustomSelect/components/UserAvatarAndName';
import { MCustomMultiSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { ROUTES } from '../../../../../constants';
import { TEAM_TYPE_DISPLAY_OPTIONS } from '../../../../../constants/teams';
import { renderItemContentForOwner } from '../../../../../routes/Settings/rules-v3/rules.utils';
import { useAuth } from '../../../../../services/auth0';
import { logger } from '../../../../../services/logger';
import { MemberLeftElementContent } from '../components/MemberLeftElementContent';

export const TeamForm: FC = () => {
  const { id = '' } = useParams();
  const { tenantId } = useAuth();
  const navigate = useNavigate();

  const {
    control,
    formState: { isValid, isDirty, errors },
    watch,
    getValues,
    setValue,
    handleSubmit,
  } = useForm<ITeamSchema>({
    resolver: zodResolver(TeamSchema),
    mode: 'onChange',
    defaultValues: {
      users: [],
    },
  });

  const watchOwner = watch('owner');
  const [selectedOwnerName, setSelectedOwnerName] = useState<string>('');

  const { mutate: doCreateTeam, isPending: createLoading } = useCreateTeam();

  const { mutate: doUpdateTeam, isPending: updateLoading } = useUpdateTeam({
    teamId: id,
  });

  const userSearchParams = useMemo(
    () => ({
      status: { in: [UserStatusEnum.ACTIVE, UserStatusEnum.INVITED] },
      type: { in: [UserTypeEnum.USER] },
    }),
    [],
  );

  const onSubmit = async (submitData: ITeam) => {
    const members = submitData.users as IUser[];
    const memberIds = members?.map(({ id: memberId }) => memberId) || [];
    const finalData = {
      name: submitData.name,
      type: submitData.type,
      userIds: [submitData.owner, ...memberIds],
      owner: submitData.owner,
    };

    finalData.owner = finalData.owner || finalData.userIds?.[0];

    if (id) {
      await doUpdateTeam(finalData, {
        onSuccess: () => navigate(ROUTES.getTeamsDetailsRoute(id)),
      });
      return;
    }
    doCreateTeam(finalData, {
      onSuccess: (data) => navigate(ROUTES.getTeamsDetailsRoute(data.id)),
    });
  };

  const onError = (error: any, e: any) => {
    logger.error(error, e);
  };
  const renderInputLeftContent = () => {
    const ownerValue = getValues('owner');
    if (ownerValue) {
      return <MAvatar name={selectedOwnerName} mr={2} />;
    }
  };
  const saveLoading = createLoading || updateLoading;
  const isDisabledSubmit = !isValid || !isDirty;

  return (
    <MCenterModal
      modalContentProps={{ 'data-testid': 'team-modal' } as any}
      modalBodyProps={{ overflow: 'visible' }}
      size="lg"
      isOpen
      closeOnOverlayClick={false}
      closeOnEsc={false}
      onClose={() => navigate(-1)}
      modalTitle={id ? 'Update Team' : 'Create New Team'}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton variant="cancel" onClick={() => navigate(-1)} minW="auto">
            Cancel
          </MButton>
          <MButton
            data-testid="teams-submit-btn"
            type="submit"
            variant="primary"
            isLoading={saveLoading}
            isDisabled={isDisabledSubmit}
            onClick={handleSubmit(onSubmit, onError)}
            minW="auto"
          >
            Create
          </MButton>
        </MStack>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <MGrid gap={4} mb={4} templateColumns="8fr 4fr">
          <MGridItem>
            <MFormField error={errors.name} label="Team Name" isRequired>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <MInput placeholder="Enter Team Name" {...field} />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem>
            <MFormField label="Team Type" isRequired error={errors.type}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    placeholder="Select"
                    items={TEAM_TYPE_DISPLAY_OPTIONS}
                    {...field}
                    isDisabled={!!id}
                  />
                )}
              />
            </MFormField>
          </MGridItem>
        </MGrid>

        <MFormField label="Choose Owner" isRequired mb={4} error={errors.owner}>
          <Controller
            name="owner"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                placeholder="Select"
                clearable
                endpoint={`/api/tenants/${tenantId}/users`}
                additionalSearchParams={userSearchParams}
                getByIdEndpointFn={(recordId) =>
                  `/api/tenants/${tenantId}/users/${recordId}`
                }
                itemTitle="name"
                itemValue="id"
                returnItem
                showQueryInput
                renderItemContent={renderItemContentForOwner}
                renderInputLeftElement
                inputLeftElementContent={renderInputLeftContent()}
                {...field}
                onChange={(user: IUser) => {
                  field.onChange(user?.id ?? '');
                  setSelectedOwnerName(user?.name ?? '');
                }}
              />
            )}
          />
        </MFormField>

        <MFormField label="Add Member">
          <Controller
            name="users"
            control={control}
            render={({ field: { value, onChange, ref, ...rest } }) => (
              <MCustomMultiSelect
                placeholder={value?.length ? '' : 'Select'}
                returnItem
                itemValue="id"
                itemTitle="name"
                showQueryInput
                loadAll
                endpoint={`/api/tenants/${tenantId}/users`}
                additionalSearchParams={userSearchParams}
                disabledItems={watchOwner ? [{ id: watchOwner }] : []}
                value={value}
                onChange={(val: IUser[]) =>
                  setValue('users', val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                renderItemContent={({ title }) => (
                  <UserAvatarAndName
                    userName={title}
                    textProps={{ fontSize: 'sm' }}
                  />
                )}
                renderInputLeftElement={!!value?.length && value.length === 1}
                inputLeftElementContent={
                  <MemberLeftElementContent value={value as IUser[]} />
                }
                showCheckCount
                closeButtonText="Apply"
                {...rest}
              />
            )}
          />
        </MFormField>
      </form>
    </MCenterModal>
  );
};
